/**
 * Postal address layouts per country, in Google libaddressinput notation.
 *
 * Generated from google-i18n-address 3.1.1, the package Saleor uses server-side to
 * normalize and validate addresses, so the dashboard lays an address out the same way
 * the API validates it. Countries are grouped by layout to keep the table small;
 * countries without an explicit layout (about 55) fall back to `DEFAULT_ADDRESS_FORMAT`.
 *
 * Placeholders: %N name, %O organization, %A street lines, %D dependent locality
 * (city area), %C city, %S country area, %Z postal code, %X sorting code, %n new line.
 * See https://github.com/google/libaddressinput/wiki/AddressValidationMetadata
 */
export const DEFAULT_ADDRESS_FORMAT = "%N%n%O%n%A%n%C";

const ADDRESS_FORMAT_GROUPS: ReadonlyArray<readonly [format: string, countryCodes: string]> = [
  [
    "%N%n%O%n%A%n%Z %C",
    "AD BA BG CY CZ DE DK DO DZ EH ET GE GL GR GW IS KG KW LA LR MA ME MG MK NE NO PL PT PY RS SJ SK SM SN TJ TM TN TZ VA XK ZM",
  ],
  ["%N%n%O%n%A%n%C%n%Z", "AC AF AI FK GB IM IO KE LK NA PN SD SH SZ TA TC VG"],
  ["%N%n%O%n%A%n%C %Z", "BH BM BN BT GU IL JO KH LB LS MT MV NP SA VC"],
  ["%O%n%N%n%A%n%Z %C %X", "BL GF GP MF MQ NC PM RE WF YT"],
  ["%N%n%O%n%A%n%C %S %Z", "AS CA FM MH MP PW UM VI"],
  ["%N%n%O%n%A%n%Z %C %S", "EE ES IT PF UY"],
  ["%O%n%N%n%A%n%C %S %Z", "AU CC CX HM NF"],
  ["%N%n%O%n%A%n%C%n%S", "PA SC SR TV"],
  ["%N%n%O%n%A%n%Z %C%n%S", "AR CL CV UZ"],
  ["%N%n%O%n%A%n%Z%n%C", "AL EC MU OM"],
  ["%O%n%N%n%A%n%Z %C", "AT BE FR NL"],
  ["%N%n%O%n%A%n%C%n%S %Z", "ID MN VN"],
  ["%N%n%O%n%A%n%C%n%S%n%Z", "EG RU UA"],
  ["%N%n%O%n%A%n%C, %S %Z", "BB SO US"],
  ["%N%n%O%n%A%n%C %X", "BF MW"],
  ["%N%n%O%n%A%n%C %Z%n%S", "IN PE"],
  ["%N%n%O%n%A%n%C, %S", "BS KN"],
  ["%N%n%O%n%A%n%S", "AE NR"],
  ["%Z%n%S%n%C%n%A%n%O%n%N", "KP KZ"],
  ["%A%n%O%n%N", "MO"],
  ["%N%n%O%n%A%n%C %S%n%Z", "CU"],
  ["%N%n%O%n%A%n%C %Z %S", "PG"],
  ["%N%n%O%n%A%n%C %Z, %S", "VE"],
  ["%N%n%O%n%A%n%C - %Z", "BD"],
  ["%N%n%O%n%A%n%C PR %Z", "PR"],
  ["%N%n%O%n%A%n%C%n%S %X", "JM"],
  ["%N%n%O%n%A%n%C%nGUERNSEY%n%Z", "GG"],
  ["%N%n%O%n%A%n%C%nJERSEY%n%Z", "JE"],
  ["%N%n%O%n%A%n%C, %S%n%Z", "HN"],
  ["%N%n%O%n%A%n%C, %Z", "MM"],
  ["%N%n%O%n%A%n%D %C%n%S %Z", "TH"],
  ["%N%n%O%n%A%n%D%n%C %Z", "NZ"],
  ["%N%n%O%n%A%n%D%n%C %Z%n%S", "NG"],
  ["%N%n%O%n%A%n%D%n%C%n%S%n%Z", "IE"],
  ["%N%n%O%n%A%n%D%n%C%n%Z", "ZA"],
  ["%N%n%O%n%A%n%D%n%C, %S, %Z", "CO"],
  ["%N%n%O%n%A%n%D%n%C-%Z", "PK"],
  ["%N%n%O%n%A%n%D%n%Z %C%n%S", "MY"],
  ["%N%n%O%n%A%n%D%n%Z %C, %S", "MX"],
  ["%N%n%O%n%A%n%D, %C%n%Z %S", "PH"],
  ["%N%n%O%n%A%n%S %Z", "KY"],
  ["%N%n%O%n%A%n%S%n%C", "KI"],
  ["%N%n%O%n%A%n%S%n%C, %Z", "LV"],
  ["%N%n%O%n%A%n%S, %C%n%Z", "CR"],
  ["%N%n%O%n%A%n%Z %C%S", "MZ"],
  ["%N%n%O%n%A%n%Z %C/%S", "TR"],
  ["%N%n%O%n%A%n%Z %S %C", "RO"],
  ["%N%n%O%n%A%n%Z%n%C%n%S", "AM"],
  ["%N%n%O%n%A%n%Z%n%C, %S", "NI"],
  ["%N%n%O%n%A%n%Z- %C", "GT"],
  ["%N%n%O%n%A%n%Z-%C%n%S", "SV"],
  ["%N%n%O%n%A%n%n%C%n%Z", "GS"],
  ["%N%n%O%n%A%nAZ %Z %C", "AZ"],
  ["%N%n%O%n%A%nFO%Z %C", "FO"],
  ["%N%n%O%n%A%nGIBRALTAR%n%Z", "GI"],
  ["%N%n%O%n%A%nHR-%Z %C", "HR"],
  ["%N%n%O%n%A%nHT%Z %C", "HT"],
  ["%N%n%O%n%A%nMC-%Z %C %X", "MC"],
  ["%N%n%O%n%A%nMD-%Z %C", "MD"],
  ["%N%n%O%n%A%nSI-%Z %C", "SI"],
  ["%N%n%O%n%A%nSINGAPORE %Z", "SG"],
  ["%N%n%O%n%C%n%A%n%Z", "HU"],
  ["%N%n%O%n%X %A %C %X", "CI"],
  ["%N%n%O%n%Z %A %C", "GN"],
  ["%O%n%N%n%A%n%C, %S%n%Z", "IQ"],
  ["%O%n%N%n%A%n%D%n%C-%S%n%Z", "BR"],
  ["%O%n%N%n%A%n%Z, %C%n%S", "BY"],
  ["%O%n%N%n%A%nAX-%Z %C%nÅLAND", "AX"],
  ["%O%n%N%n%A%nCH-%Z %C", "CH"],
  ["%O%n%N%n%A%nFI-%Z %C", "FI"],
  ["%O%n%N%n%A%nFL-%Z %C", "LI"],
  ["%O%n%N%n%A%nL-%Z %C", "LU"],
  ["%O%n%N%n%A%nLT-%Z %C %S", "LT"],
  ["%O%n%N%n%A%nSE-%Z %C", "SE"],
  ["%O%n%N%n%S%n%C, %D%n%A%n%Z", "IR"],
  ["%S %C%D%n%A%n%O%n%N%n%Z", "KR"],
  ["%S%n%C%n%A%n%O%n%N", "HK"],
  ["%Z%n%S%C%D%n%A%n%O%n%N", "CN"],
  ["%Z%n%S%C%n%A%n%O%n%N", "TW"],
  ["〒%Z%n%S%n%A%n%O%n%N", "JP"],
];

export const ADDRESS_FORMATS_BY_COUNTRY: Readonly<Record<string, string>> = Object.fromEntries(
  ADDRESS_FORMAT_GROUPS.flatMap(([format, countryCodes]) =>
    countryCodes.split(" ").map(countryCode => [countryCode, format] as const),
  ),
);
